import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { RESPONSIVE_QUERY } from '@pass-frontend/utility/style-helpers';
import { FacilityHeader } from '../facility-header/facility-header';

const WidthConstrainer = styled.div`
  max-width: 1272px;
  margin: 0 auto;
  height: 100vh;
  ${RESPONSIVE_QUERY.large} {
    width: 90%;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
`;

const UserInfoWrapper = styled.div`
  background-color: #f8f9fa;
  padding: 24px;
  border-radius: 8px;
  margin: 24px 0;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const LogoutButton = styled.button`
  background-color: #dc3545;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
  margin-top: 24px;

  &:hover {
    background-color: #c82333;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const RefreshButton = styled.button`
  background-color: #007bff;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
  margin-top: 16px;
  margin-right: 16px;

  &:hover {
    background-color: #0056b3;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

interface UserInfo {
  firstName?: string;
  lastName?: string;
  email?: string;
  username?: string;
}

interface SessionStatus {
  isValid: boolean;
  expired: boolean;
  needsRefresh: boolean;
  expiresAt?: string;
  sessionToken?: string;
  error?: string;
  debug?: Record<string, unknown>;
}

interface TokenRefreshResponse {
  success: boolean;
  expiresAt: string;
  sessionToken: string;
  error?: string;
  debug?: Record<string, unknown>;
}

class CrossDomainSessionManager {
  private baseUrl: string;
  private sessionToken: string | null = null;
  private readonly SESSION_STORAGE_KEY = 'facility_session_token';
  private readonly SESSION_EXPIRY_KEY = 'facility_session_expiry';
  private isRefreshing = false; // Simple flag to prevent concurrent refreshes
  private lastRefreshData: TokenRefreshResponse | null = null;

  constructor() {
    this.baseUrl =
      process.env.NEXT_PUBLIC_PASS_EDGE_BASE_URL ||
      (typeof window !== 'undefined' ? window.location.origin : '');

    this.loadSessionFromStorage();
  }

  private debugLog = (operation: string, data: Record<string, unknown>) => {
    console.log(`[CrossDomainSession] ${operation}:`, {
      timestamp: new Date().toISOString(),
      ...data,
    });
  };

  private debugError = (
    operation: string,
    error: Error | unknown,
    additionalData?: Record<string, unknown>,
  ) => {
    console.error(`[CrossDomainSession] ${operation} ERROR:`, {
      timestamp: new Date().toISOString(),
      error: error instanceof Error ? error.message : String(error),
      ...additionalData,
    });
  };

  private loadSessionFromStorage(): void {
    if (typeof window === 'undefined') return;

    try {
      const storedToken = localStorage.getItem(this.SESSION_STORAGE_KEY);
      const storedExpiry = localStorage.getItem(this.SESSION_EXPIRY_KEY);

      if (storedToken && storedExpiry) {
        const expiryDate = new Date(storedExpiry);
        const now = new Date();

        if (expiryDate > now) {
          this.sessionToken = storedToken;
          this.debugLog('Session loaded from localStorage', {
            expiresAt: storedExpiry,
            timeUntilExpiryMinutes: Math.round(
              (expiryDate.getTime() - now.getTime()) / (1000 * 60),
            ),
          });
        } else {
          this.debugLog('Stored session expired, clearing', {});
          this.clearStoredSession();
        }
      }
    } catch (error) {
      this.debugError('Error loading session from localStorage', error);
      this.clearStoredSession();
    }
  }

  private saveSessionToStorage(token: string, expiresAt?: string): void {
    if (typeof window === 'undefined') return;

    try {
      localStorage.setItem(this.SESSION_STORAGE_KEY, token);
      if (expiresAt) {
        localStorage.setItem(this.SESSION_EXPIRY_KEY, expiresAt);
      }

      this.debugLog('Session saved to localStorage', {
        tokenLength: token.length,
        expiresAt,
      });
    } catch (error) {
      this.debugError('Error saving session to localStorage', error);
    }
  }

  private clearStoredSession(): void {
    if (typeof window === 'undefined') return;

    try {
      localStorage.removeItem(this.SESSION_STORAGE_KEY);
      localStorage.removeItem(this.SESSION_EXPIRY_KEY);
      this.debugLog('Stored session cleared', {});
    } catch (error) {
      this.debugError('Error clearing stored session', error);
    }
  }

  async initializeFromUrl(): Promise<boolean> {
    if (typeof window === 'undefined') return false;

    this.debugLog('Initializing session from URL', {
      hasStoredToken: !!this.sessionToken,
      currentUrl: window.location.href,
    });

    if (this.sessionToken) {
      const isValid = await this.validateTokenWithBackend(this.sessionToken);
      if (isValid) {
        this.debugLog('Stored token is valid', {});
        return true;
      } else {
        this.debugLog('Stored token is invalid, clearing', {});
        this.clearSession();
      }
    }

    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('sessionToken');

    if (tokenFromUrl) {
      this.sessionToken = tokenFromUrl;
      const isValid = await this.validateTokenWithBackend(tokenFromUrl);

      if (isValid) {
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('sessionToken');
        window.history.replaceState({}, '', newUrl.toString());

        this.debugLog('Token validated and URL cleaned', {});
        return true;
      } else {
        this.debugError('Token validation failed', null);
        this.clearSession();
        return false;
      }
    }

    this.debugLog('No session token found', {});
    return false;
  }

  private async validateTokenWithBackend(token: string): Promise<boolean> {
    try {
      const response = await fetch(
        `${this.baseUrl}/rest/pass-edge/ohid/facility/session/validate`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-session-token': token,
          },
        },
      );

      if (response.ok) {
        const sessionData = await response.json();

        if (sessionData.isValid && !sessionData.expired) {
          this.debugLog('Token validation successful, updating localStorage', {
            backendExpiry: sessionData.expiresAt,
          });

          if (sessionData.expiresAt) {
            this.saveSessionToStorage(token, sessionData.expiresAt);
          } else {
            this.saveSessionToStorage(token);
          }

          return true;
        } else {
          this.debugLog('Session is invalid or expired', {
            isValid: sessionData.isValid,
            expired: sessionData.expired,
          });
          return false;
        }
      } else {
        this.debugError('Token validation request failed', null, {
          status: response.status,
        });
        return false;
      }
    } catch (error) {
      this.debugError('Token validation error', error);
      return false;
    }
  }

  getSessionToken(): string | null {
    return this.sessionToken;
  }

  clearSession(): void {
    this.debugLog('Clearing session', {});
    this.sessionToken = null;
    this.isRefreshing = false;
    this.clearStoredSession();
  }

  async makeApiRequest(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<Response> {
    const fullUrl = `${this.baseUrl}${endpoint}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (options.headers) {
      if (options.headers instanceof Headers) {
        options.headers.forEach((value, key) => {
          headers[key] = value;
        });
      } else if (Array.isArray(options.headers)) {
        options.headers.forEach(([key, value]) => {
          headers[key] = value;
        });
      } else {
        Object.assign(headers, options.headers);
      }
    }

    if (this.sessionToken) {
      headers['x-session-token'] = this.sessionToken;
    }

    const requestOptions: RequestInit = {
      ...options,
      headers,
    };

    try {
      const response = await fetch(fullUrl, requestOptions);

      if (response.status === 401) {
        this.debugLog('Received 401, clearing session', {});
        this.clearSession();
      }

      return response;
    } catch (error) {
      this.debugError(`Network error for ${endpoint}`, error);
      throw error;
    }
  }

  async refreshToken(): Promise<TokenRefreshResponse | null> {
    if (this.isRefreshing) {
      this.debugLog(
        'Refresh already in progress, skipping duplicate request',
        {},
      );
      return null;
    }

    if (!this.sessionToken) {
      this.debugLog('Cannot refresh token - no session token', {});
      return null;
    }

    this.isRefreshing = true;

    try {
      this.debugLog('Starting token refresh', {
        currentTokenLength: this.sessionToken?.length,
      });

      const response = await this.makeApiRequest(
        '/rest/pass-edge/ohid/facility/token/refresh',
        {
          method: 'POST',
        },
      );

      if (response.ok) {
        const refreshData: TokenRefreshResponse = await response.json();

        this.debugLog('Token refresh successful', {
          newExpiresAt: refreshData.expiresAt,
          hasNewToken: !!refreshData.sessionToken,
          success: refreshData.success,
        });

        if (refreshData.sessionToken && refreshData.success) {
          // Update session token
          this.sessionToken = refreshData.sessionToken;
          this.lastRefreshData = refreshData;

          this.debugLog('Session token updated', {
            newExpiresAt: refreshData.expiresAt,
          });

          // Save with backend expiry time
          this.saveSessionToStorage(
            refreshData.sessionToken,
            refreshData.expiresAt,
          );

          // Small delay to ensure session is properly updated
          await new Promise((resolve) => setTimeout(resolve, 100));

          return refreshData;
        } else {
          this.debugError(
            'Token refresh response missing required fields',
            null,
            {
              hasSessionToken: !!refreshData.sessionToken,
              success: refreshData.success,
            },
          );
          return null;
        }
      } else {
        this.debugError('Token refresh failed', null, {
          status: response.status,
        });
        return null;
      }
    } catch (error) {
      this.debugError('Token refresh error', error);
      return null;
    } finally {
      this.isRefreshing = false;
    }
  }

  getLastRefreshData(): TokenRefreshResponse | null {
    return this.lastRefreshData;
  }

  isCurrentlyRefreshing(): boolean {
    return this.isRefreshing;
  }
}

const sessionManager = new CrossDomainSessionManager();

const debugLog = (operation: string, data: Record<string, unknown>) => {
  console.log(`[FacilityPartnerPortal] ${operation}:`, {
    timestamp: new Date().toISOString(),
    ...data,
  });
};

const debugError = (
  operation: string,
  error: Error | unknown,
  additionalData?: Record<string, unknown>,
) => {
  console.error(`[FacilityPartnerPortal] ${operation} ERROR:`, {
    timestamp: new Date().toISOString(),
    error: error instanceof Error ? error.message : String(error),
    ...additionalData,
  });
};

export function FacilityPartnerPortalHomePage() {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionStatus, setSessionStatus] = useState<SessionStatus | null>(
    null,
  );
  const [refreshing, setRefreshing] = useState(false);
  const [authenticationErrors, setAuthenticationErrors] = useState<string[]>(
    [],
  );
  const [isSessionInitialized, setIsSessionInitialized] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const initializeSession = async () => {
      debugLog('Component initialization', {});
      setLoading(true);
      setAuthenticationErrors([]);

      try {
        const sessionInitialized = await sessionManager.initializeFromUrl();

        if (sessionInitialized) {
          setIsSessionInitialized(true);
        } else {
          window.location.href = '/facility-sign-in';
          return;
        }
      } catch (error) {
        debugError('Session initialization failed', error);
        setAuthenticationErrors([
          'Session initialization failed: ' + (error as Error)?.message,
        ]);
        window.location.href = '/facility-sign-in';
        return;
      }
    };

    initializeSession();
  }, []);

  // Reconcile localStorage expiry (later value wins) after session init
  useEffect(() => {
    if (!isSessionInitialized) return;
    if (typeof window === 'undefined') return;
    const lsExpiry = localStorage.getItem('facility_session_expiry');
    if (!lsExpiry) return;
    setSessionStatus((prev) => {
      if (!prev) {
        return {
          isValid: true,
          expired: false,
          needsRefresh: false,
          expiresAt: lsExpiry,
          sessionToken: sessionManager.getSessionToken() ?? undefined,
        };
      }
      if (!prev.expiresAt || new Date(lsExpiry) > new Date(prev.expiresAt)) {
        return {
          ...prev,
          expiresAt: lsExpiry,
          isValid: true,
          expired: false,
          needsRefresh: false,
        };
      }
      return prev;
    });
  }, [isSessionInitialized]);

  const validateAndRefreshSession = useCallback(async (): Promise<boolean> => {
    try {
      if (!sessionManager.getSessionToken()) {
        setAuthenticationErrors((prev) => [...prev, 'No session token found']);
        return false;
      }

      // Skip validation if already refreshing to prevent race conditions
      if (sessionManager.isCurrentlyRefreshing()) {
        debugLog('Skipping validation - refresh in progress', {});
        return true;
      }

      const validateResponse = await sessionManager.makeApiRequest(
        '/rest/pass-edge/ohid/facility/session/validate',
      );

      if (validateResponse.ok) {
        const sessionData: SessionStatus = await validateResponse.json();

        const now = new Date();
        const expiresAt = sessionData.expiresAt
          ? new Date(sessionData.expiresAt)
          : null;
        const timeUntilExpiryMs = expiresAt
          ? expiresAt.getTime() - now.getTime()
          : 0;
        const timeUntilExpiryMinutes = Math.round(
          timeUntilExpiryMs / (1000 * 60),
        );

        debugLog('Session validation response', {
          isValid: sessionData.isValid,
          expired: sessionData.expired,
          needsRefresh: sessionData.needsRefresh,
          timeUntilExpiryMinutes,
        });

        setSessionStatus(sessionData);

        // Merge with potentially later localStorage expiry
        const lsExpiry =
          typeof window !== 'undefined'
            ? localStorage.getItem('facility_session_expiry')
            : null;
        if (
          lsExpiry &&
          (!sessionData.expiresAt ||
            new Date(lsExpiry) > new Date(sessionData.expiresAt))
        ) {
          setSessionStatus({ ...sessionData, expiresAt: lsExpiry });
        }

        if (sessionData.expired) {
          debugLog('Session expired, redirecting', {});
          sessionManager.clearSession();
          setAuthenticationErrors((prev) => [...prev, 'Session expired']);
          window.location.href = '/facility-sign-in';
          return false;
        }

        if (
          sessionData.needsRefresh &&
          !sessionManager.isCurrentlyRefreshing()
        ) {
          debugLog('Session needs refresh - starting refresh process', {
            timeUntilExpiryMinutes,
          });

          setRefreshing(true);
          try {
            const refreshResult = await sessionManager.refreshToken();

            if (refreshResult) {
              debugLog('Token refresh successful', {});
              setAuthenticationErrors([]);

              // Immediately reflect refreshed expiry without waiting for new validation
              setSessionStatus((prev) => ({
                ...(prev ?? {
                  isValid: true,
                  expired: false,
                  needsRefresh: false,
                }),
                isValid: true,
                expired: false,
                needsRefresh: false,
                expiresAt: refreshResult.expiresAt,
                sessionToken: refreshResult.sessionToken,
              }));
            } else {
              debugLog(
                'Token refresh failed, continuing with current session',
                {},
              );
              setAuthenticationErrors((prev) => [
                ...prev,
                'Token refresh failed, but session is still valid',
              ]);
            }
          } catch (refreshError) {
            debugError('Token refresh error', refreshError);
            setAuthenticationErrors((prev) => [
              ...prev,
              'Token refresh error: ' + (refreshError as Error)?.message,
            ]);
          } finally {
            setRefreshing(false);
          }
        }

        return sessionData.isValid;
      } else {
        debugError('Session validation failed', null, {
          status: validateResponse.status,
        });

        sessionManager.clearSession();
        setAuthenticationErrors((prev) => [
          ...prev,
          `Session validation failed: ${validateResponse.status}`,
        ]);
        window.location.href = '/facility-sign-in';
        return false;
      }
    } catch (error) {
      debugError('Session validation error', error);
      sessionManager.clearSession();
      setAuthenticationErrors((prev) => [
        ...prev,
        'Session validation error: ' + (error as Error)?.message,
      ]);
      window.location.href = '/facility-sign-in';
      return false;
    }
  }, []);

  const fetchUserInfo = useCallback(async () => {
    if (!sessionManager.getSessionToken()) {
      return;
    }

    try {
      const response = await sessionManager.makeApiRequest(
        '/rest/pass-edge/ohid/facility/user-info',
      );

      if (response.ok) {
        const data = await response.json();
        setUserInfo(data);
      } else {
        setAuthenticationErrors((prev) => [
          ...prev,
          `User info fetch failed: ${response.status}`,
        ]);
      }
    } catch (error) {
      debugError('User info fetch error', error);
      setAuthenticationErrors((prev) => [
        ...prev,
        'User info fetch error: ' + (error as Error)?.message,
      ]);
    }
  }, []);

  useEffect(() => {
    const loadUserData = async () => {
      if (!isSessionInitialized) return;

      setLoading(true);
      const isSessionValid = await validateAndRefreshSession();

      if (isSessionValid) {
        await fetchUserInfo();
      }

      setLoading(false);
    };

    loadUserData();
  }, [isSessionInitialized, validateAndRefreshSession, fetchUserInfo]);

  useEffect(() => {
    if (!isSessionInitialized || !sessionManager.getSessionToken()) {
      return;
    }

    const interval = setInterval(
      () => {
        if (!loading && !refreshing && sessionManager.getSessionToken()) {
          validateAndRefreshSession();
        }
      },
      4 * 60 * 1000,
    ); // Check every 4 minutes

    return () => clearInterval(interval);
  }, [validateAndRefreshSession, loading, refreshing, isSessionInitialized]);

  useEffect(() => {
    if (sessionStatus?.expiresAt) {
      const expiresAt = new Date(sessionStatus.expiresAt);
      const now = new Date();
      const timeUntilExpiry = expiresAt.getTime() - now.getTime();
      const oneMinute = 60 * 1000;

      if (timeUntilExpiry > oneMinute) {
        const warningTimeout = setTimeout(() => {
          console.warn('Session will expire in 1 minute');
        }, timeUntilExpiry - oneMinute);

        return () => clearTimeout(warningTimeout);
      }
    }
    return undefined;
  }, [sessionStatus?.expiresAt]);

  const handleLogout = () => {
    if (typeof window === 'undefined') return;

    sessionManager.clearSession();

    const baseUrl =
      process.env.NEXT_PUBLIC_PASS_EDGE_BASE_URL || window.location.origin;
    const logoutUrl =
      process.env.NEXT_PUBLIC_OHID_PARTNER_LOGOUT_URL ||
      `${baseUrl}/rest/pass-edge/ohid/facility/logout`;

    window.location.href = logoutUrl;
  };

  const handleManualRefresh = async () => {
    if (!sessionManager.getSessionToken()) {
      setAuthenticationErrors((prev) => [
        ...prev,
        'No session token available for refresh',
      ]);
      return;
    }

    if (sessionManager.isCurrentlyRefreshing()) {
      return;
    }

    const oldToken = sessionManager.getSessionToken();
    setRefreshing(true);

    try {
      const refreshResult = await sessionManager.refreshToken();

      if (refreshResult) {
        const newToken = refreshResult.sessionToken;

        if (oldToken === newToken) {
          setAuthenticationErrors((prev) => [
            ...prev,
            'Token refresh failed - session token did not change',
          ]);
          return;
        }

        setAuthenticationErrors([]);

        setSessionStatus({
          isValid: true,
          expired: false,
          needsRefresh: false,
          expiresAt: refreshResult.expiresAt,
          sessionToken: newToken,
        });

        // Fetch updated user info with new token
        await fetchUserInfo();

        debugLog('Manual refresh completed', {
          oldToken: oldToken?.substring(0, 20) + '...',
          newToken: newToken?.substring(0, 20) + '...',
          newExpiry: refreshResult.expiresAt,
        });
      } else {
        setAuthenticationErrors((prev) => [
          ...prev,
          'Manual token refresh failed',
        ]);
      }
    } catch (error) {
      setAuthenticationErrors((prev) => [
        ...prev,
        'Manual token refresh error: ' + (error as Error)?.message,
      ]);
    } finally {
      setRefreshing(false);
    }
  };

  const getUserDisplayName = () => {
    if (userInfo?.firstName && userInfo?.lastName) {
      return `${userInfo.firstName} ${userInfo.lastName}`;
    } else if (userInfo?.username) {
      return userInfo.username;
    } else if (userInfo?.email) {
      return userInfo.email;
    }
    return 'Partner User';
  };

  const showDebugInfo =
    process.env.NODE_ENV !== 'production' && authenticationErrors.length > 0;

  if (!isSessionInitialized) {
    return (
      <>
        <FacilityHeader shouldHideSignInButton={false} />
        <div className="has-rds-mb-none has-rds-bg-grey-lightest">
          <div className="has-rds-bg-grey-lightest has-rds-pb-64">
            <WidthConstrainer className="has-rds-pv-40">
              <ContentWrapper>
                <h1 className="is-rds-h1 has-rds-pv-40 has-text-centered">
                  Welcome to Partner Portal
                </h1>
                <p className="is-rds-body-large has-text-centered has-rds-mb-24">
                  Initializing secure session...
                </p>
              </ContentWrapper>
            </WidthConstrainer>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <FacilityHeader shouldHideSignInButton={false} />
      <div className="has-rds-mb-none has-rds-bg-grey-lightest">
        <div className="has-rds-bg-grey-lightest has-rds-pb-64">
          <WidthConstrainer className="has-rds-pv-40">
            <ContentWrapper>
              <h1 className="is-rds-h1 has-rds-pv-40 has-text-centered">
                Welcome to Partner Portal
              </h1>

              {showDebugInfo && (
                <div
                  style={{
                    background: '#fff3cd',
                    border: '1px solid #ffeaa7',
                    padding: '16px',
                    borderRadius: '4px',
                    margin: '16px 0',
                    maxWidth: '600px',
                  }}
                >
                  <h3 style={{ margin: '0 0 8px 0', color: '#856404' }}>
                    Authentication Debug Info:
                  </h3>
                  {authenticationErrors.map((error, index) => (
                    <div
                      key={index}
                      style={{
                        fontSize: '12px',
                        color: '#856404',
                        margin: '4px 0',
                      }}
                    >
                      • {error}
                    </div>
                  ))}
                </div>
              )}

              {loading ? (
                <p className="is-rds-body-large has-text-centered has-rds-mb-24">
                  {refreshing ? 'Refreshing session...' : 'Loading...'}
                </p>
              ) : (
                <UserInfoWrapper>
                  <h2 className="is-rds-h3 has-rds-mb-16">
                    Hello, {getUserDisplayName()}!
                  </h2>
                  {userInfo?.email && (
                    <p className="is-rds-body has-rds-mb-8">
                      Email: {userInfo.email}
                    </p>
                  )}
                  <p className="is-rds-body-large">
                    You have successfully authenticated and can now access the
                    facility partner portal.
                  </p>
                  {sessionStatus?.expiresAt && (
                    <p className="is-rds-body-small has-rds-mt-16">
                      Session expires:{' '}
                      {new Date(sessionStatus.expiresAt).toLocaleString()}
                    </p>
                  )}
                </UserInfoWrapper>
              )}

              <ButtonContainer>
                <RefreshButton
                  onClick={handleManualRefresh}
                  disabled={loading || refreshing}
                >
                  Refresh Session
                </RefreshButton>
                <LogoutButton onClick={handleLogout} disabled={loading}>
                  Sign Out
                </LogoutButton>
              </ButtonContainer>
            </ContentWrapper>
          </WidthConstrainer>
        </div>
      </div>
    </>
  );
}
